import { fieldIds } from 'src/util';
import { getTypeValidationKeys } from 'src/util/typeValidationMessages';
import * as y from 'yup';

const typeValidationKeys = getTypeValidationKeys();

export enum UnitType {
  Apartment = 'Apartment',
  Basement = 'Basement',
  Building = 'Building',
  Condo = 'Condo',
  Department = 'Department',
  Floor = 'Floor',
  Front = 'Front',
  Hangar = 'Hangar',
  Key = 'Key',
  Lobby = 'Lobby',
  Lot = 'Lot',
  Lower = 'Lower',
  Office = 'Office',
  Penthouse = 'Penthouse',
  Pier = 'Pier',
  Rear = 'Rear',
  Room = 'Room',
  Side = 'Side',
  Space = 'Space',
  Stop = 'Stop',
  Suite = 'Suite',
  Trailer = 'Trailer',
  Unit = 'Unit',
  Upper = 'Upper',
}

export const PropertyContract = y.object({
  loanPurposeType: y.string().required(),
  loanPurposeTypeOtherDescription: y.string().optional(),
  loanPurposeTypeUrla: y.string().optional(),
  addressLineText: y
    .string()
    .transform((value, originalValue) => {
      // Convert empty strings to undefined for proper optional handling
      return originalValue === '' ? undefined : value;
    })
    .when(['loanPurposeType', '$loan'], (loanPurposeType, loan) => {
      if (isPreApproval(loanPurposeType, loan)) {
        return y
          .string()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .optional();
      }
      return y
        .string()
        .transform((value, originalValue) => {
          return originalValue === '' ? undefined : value;
        })
        .required();
    }),
  city: y
    .string()
    .transform((value, originalValue) => {
      // Convert empty strings to undefined for proper optional handling
      return originalValue === '' ? undefined : value;
    })
    .when(['loanPurposeType', '$loan'], (loanPurposeType, loan) => {
      // City is required for both pre-approval and regular loans
      return y
        .string()
        .transform((value, originalValue) => {
          return originalValue === '' ? undefined : value;
        })
        .required();
    }),
  state: y
    .string()
    .transform((value, originalValue) => {
      // Convert empty strings to undefined for proper optional handling
      return originalValue === '' ? undefined : value;
    })
    .when(['loanPurposeType', '$loan'], (loanPurposeType, loan) => {
      // State is required for both pre-approval and regular loans
      return y
        .string()
        .transform((value, originalValue) => {
          return originalValue === '' ? undefined : value;
        })
        .required();
    }),
  postalCode: y
    .string()
    .transform((value, originalValue) => {
      // Convert empty strings to undefined for proper optional handling
      return originalValue === '' ? undefined : value;
    })
    .when(['loanPurposeType', '$loan'], (loanPurposeType, loan) => {
      if (isPreApproval(loanPurposeType, loan)) {
        return y
          .string()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .optional();
      }
      return y
        .string()
        .transform((value, originalValue) => {
          return originalValue === '' ? undefined : value;
        })
        .required();
    }),
  county: y
    .string()
    .transform((value, originalValue) => {
      // Convert empty strings to undefined for proper optional handling
      return originalValue === '' ? undefined : value;
    })
    .when(['loanPurposeType', '$loan'], (loanPurposeType, loan) => {
      if (isPreApproval(loanPurposeType, loan)) {
        return y
          .string()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .optional();
      }
      return y
        .string()
        .transform((value, originalValue) => {
          return originalValue === '' ? undefined : value;
        })
        .required();
    }),
  refinancePropertyAcquiredYear: y
    .string()
    .optional()
    .when('loanPurposeType', (loanPurposeType) => {
      // Handle both array and string cases
      const purposeValue = Array.isArray(loanPurposeType)
        ? loanPurposeType[0]
        : loanPurposeType;
      if (purposeValue?.includes('Refinance')) {
        return y
          .string()
          .required()
          .test('not-empty-or-placeholder', (value) => {
            // Handle common dropdown "empty" states
            return !!(
              value &&
              value !== '' &&
              value !== 'Select' &&
              value !== 'select'
            );
          });
      }
      return y.string().optional();
    }),
  financedNumberOfUnits: y.number(),
  refinancePropertyOriginalCostAmount: y
    .number()
    .transform((value, originalValue) => {
      // Convert empty strings to undefined for proper optional handling
      return originalValue === '' ? undefined : value;
    })
    .optional()
    .when('loanPurposeType', (loanPurposeType) => {
      // Handle both array and string cases
      const purposeValue = Array.isArray(loanPurposeType)
        ? loanPurposeType[0]
        : loanPurposeType;

      if (purposeValue?.includes('Refinance')) {
        return y
          .number()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .required();
      }
      return y
        .number()
        .transform((value, originalValue) => {
          return originalValue === '' ? undefined : value;
        })
        .optional();
    }),
  refinancePropertyExistingLienAmount: y
    .number()
    .transform((value, originalValue) => {
      // Convert empty strings to undefined for proper optional handling
      return originalValue === '' ? undefined : value;
    })
    .optional()
    .when('loanPurposeType', (loanPurposeType) => {
      if (loanPurposeType?.includes('Refinance')) {
        return y
          .number()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .required();
      }
      return y
        .number()
        .transform((value, originalValue) => {
          return originalValue === '' ? undefined : value;
        })
        .optional();
    }),
  addressUnitDesignatorType: y
    .mixed<UnitType>()
    .oneOf(Object.values(UnitType))
    .transform((value) => (value === '' ? undefined : value))
    .optional(),
  addressUnitIdentifier: y.string().optional(),
});

const isPreApproval = (loanPurposeType, loan) => {
  return (
    typeof loanPurposeType === 'string' &&
    (loanPurposeType.toLowerCase() === 'pre-approval' ||
      (loanPurposeType.toLowerCase() === 'purchase' &&
        loan?.customFields[fieldIds.isPreApproval] === 'true'))
  );
};

export interface Property extends y.Asserts<typeof PropertyContract> {}

export const propertyNewValidationErrorMap = {
  _entityName: 'Property',
  loan: {
    'about-property': {
      property: {
        addressUnitDesignatorType:
          typeValidationKeys.property.addressUnitDesignatorType,
        addressUnitIdentifier:
          typeValidationKeys.property.addressUnitIdentifier,
        city: typeValidationKeys.property.city,
        state: typeValidationKeys.property.state,
        financedNumberOfUnits:
          typeValidationKeys.property.financedNumberOfUnits,
        refinancePropertyAcquiredYear:
          typeValidationKeys.property.refinancePropertyAcquiredYear,
        refinancePropertyOriginalCostAmount:
          'types.validation.property.refinancePropertyOriginalCostAmount',
      },
    },
    purpose: {
      property: {
        loanPurposeType: typeValidationKeys.property.loanPurposeType,
      },
    },
    'loan-amount': {
      property: {
        refinancePropertyExistingLienAmount:
          typeValidationKeys.property.refinancePropertyExistingLienAmount,
      },
    },
  },
};

export const propertyNewValidationErrorMapFull = {
  _entityName: 'Property',
  loan: {
    'about-property': {
      property: {
        addressUnitDesignatorType:
          typeValidationKeys.property.addressUnitDesignatorType,
        addressUnitIdentifier:
          typeValidationKeys.property.addressUnitIdentifier,
        addressLineText: typeValidationKeys.property.addressLineText,
        city: typeValidationKeys.property.city,
        state: typeValidationKeys.property.state,
        postalCode: typeValidationKeys.property.postalCode,
        county: typeValidationKeys.property.county,
        financedNumberOfUnits:
          typeValidationKeys.property.financedNumberOfUnits,
        refinancePropertyAcquiredYear:
          typeValidationKeys.property.refinancePropertyAcquiredYear,
        refinancePropertyOriginalCostAmount:
          'types.validation.property.refinancePropertyOriginalCostAmount',
      },
    },
    purpose: {
      property: {
        loanPurposeType: typeValidationKeys.property.loanPurposeType,
      },
    },
    'loan-amount': {
      property: {
        refinancePropertyExistingLienAmount:
          typeValidationKeys.property.refinancePropertyExistingLienAmount,
      },
    },
  },
};
